import { Loader2, RefreshCw } from "lucide-react"
import { useMemo, useState } from "react"
import type { TaskSessionDebugInfo } from "../../store/TaskModel"
import { buildTaskSessionTree, isTaskSessionActive, type TaskSessionTreeNode } from "./taskSessionTree"
import { TaskSessionCard } from "./TaskSessionCard"

interface TaskSessionsPanelProps {
    taskId: string
    sessions: TaskSessionDebugInfo[]
    loading: boolean
    error: string | null
    onRefresh: () => Promise<void>
}

export function TaskSessionsPanel({ taskId, sessions, loading, error, onRefresh }: TaskSessionsPanelProps): JSX.Element {
    const [refreshing, setRefreshing] = useState(false)
    const roots = useMemo(() => buildTaskSessionTree(sessions), [sessions])
    const auxiliarySessions = sessions.filter((session) => session.role !== "primary")
    const activeCount = auxiliarySessions.filter((session) => isTaskSessionActive(session.status)).length

    const refresh = async () => {
        if (refreshing) return
        setRefreshing(true)
        try {
            await onRefresh()
        } finally {
            setRefreshing(false)
        }
    }

    return (
        <section className="flex h-full min-h-0 flex-col bg-base-100 text-base-content" aria-label="Task session tree">
            <div className="flex h-9 shrink-0 items-center justify-between gap-3 border-b border-border px-2">
                <div className="min-w-0 truncate text-[11px] text-muted">
                    {auxiliarySessions.length} sub-agent{auxiliarySessions.length === 1 ? "" : "s"}
                    {activeCount > 0 ? ` · ${activeCount} active` : ""}
                </div>
                <button
                    type="button"
                    className="btn flex h-7 w-7 shrink-0 items-center justify-center text-muted hover:bg-base-200 hover:text-base-content disabled:opacity-50"
                    onClick={() => void refresh()}
                    disabled={loading || refreshing}
                    title="Refresh sessions"
                    aria-label="Refresh sessions"
                >
                    {loading || refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
                {error && <div className="border-b border-border bg-warning/10 px-3 py-2 text-xs text-warning">{error}</div>}
                {roots.length === 0 && !loading ? (
                    <div className="px-3 py-4 text-xs text-muted">No task sessions found.</div>
                ) : (
                    <div role="tree" aria-label="Task sessions" className="flex flex-col gap-1 px-2 py-2">
                        <SessionTreeCards taskId={taskId} taskSessions={sessions} nodes={roots} />
                    </div>
                )}
                {loading && sessions.length === 0 && (
                    <div className="flex items-center gap-2 px-3 py-4 text-xs text-muted">
                        <Loader2 size={13} className="animate-spin" />
                        Loading sessions…
                    </div>
                )}
            </div>
        </section>
    )
}

function SessionTreeCards({
    taskId,
    taskSessions,
    nodes,
}: {
    taskId: string
    taskSessions: readonly TaskSessionDebugInfo[]
    nodes: TaskSessionTreeNode<TaskSessionDebugInfo>[]
}): JSX.Element {
    return (
        <>
            {nodes.map((node) => {
                const session = node.session
                return (
                    <div key={session.key} role="treeitem">
                        <TaskSessionCard taskId={taskId} session={session} taskSessions={taskSessions} childCount={node.children.length} />
                        {node.children.length > 0 && (
                            <div role="group" className="ml-3 mt-1 flex flex-col gap-1 border-l border-border pl-1.5">
                                <SessionTreeCards taskId={taskId} taskSessions={taskSessions} nodes={node.children} />
                            </div>
                        )}
                    </div>
                )
            })}
        </>
    )
}
